import React, { startTransition, useDeferredValue, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, ZoomControl } from 'react-leaflet';
import { TextBox } from 'devextreme-react/text-box';
import { SelectBox } from 'devextreme-react/select-box';
import { Popup } from 'devextreme-react/popup';
import { useNavigate, useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PublicSearch.css';
import { cemeteryApi, visitsApi } from '../services/api';
import type { CemeteryData, CemeterySummary } from '../types';
import DistortableImageLayer from '../components/DistortableImageLayer';
import { createPlacementFromBounds, getOverlayPlacement, getReferencePointIconOption, projectReferencePoint } from '../lib/cemeteryOverlay';

// Fix for default marker icons in React Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const FALLBACK_CEMETERY_BOUNDS = {
  name: "Pokopališče Zagorje ob Savi",
  southwest: [46.133992, 14.997715] as [number, number],
  northeast: [46.135867, 14.999685] as [number, number],
  center: [46.1349295, 14.9987] as [number, number],
  zoom: 19
};

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const createReferencePointMapIcon = (iconKey: string, name: string): L.DivIcon => {
  const iconOption = getReferencePointIconOption(iconKey);

  return L.divIcon({
    className: 'public-reference-point-wrapper',
    iconSize: [48, 58],
    iconAnchor: [24, 56],
    popupAnchor: [0, -44],
    html: `
      <div class="public-reference-point-marker" style="--rp-color:${iconOption.color};">
        <div class="public-reference-point-glyph">${escapeHtml(iconOption.glyph)}</div>
      </div>
      <div class="public-reference-point-label">${escapeHtml(name)}</div>
    `,
  });
};

const SEARCH_RESULTS_PAGE_SIZE = 5;

interface DeceasedPerson {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  maidenName?: string;
  fullName: string;
  birthDate?: string;
  deathDate?: string;
  displayBirthDate?: string;
  displayDeathDate?: string;
  age?: number;
  graveType?: string;
  graveSiteId: number;
  graveCode?: string;
  latitude?: number;
  longitude?: number;
  photoPath?: string;
  cemeteryName?: string;
  cemeterySlug?: string;
}

// Component to fly to a location when selected
const FlyToLocation: React.FC<{ position: [number, number] }> = React.memo(({ position }) => {
  const map = useMap();
  const lastPositionRef = useRef<string>('');
  
  useEffect(() => {
    if (position && position[0] && position[1]) {
      const positionKey = `${position[0]},${position[1]}`;
      // Only fly if position actually changed
      if (positionKey !== lastPositionRef.current) {
        lastPositionRef.current = positionKey;
        map.flyTo(position, 20, {
          duration: 1.5
        });
      }
    }
  }, [position, map]);
  
  return null;
});

const PublicSearch: React.FC = () => {
  const navigate = useNavigate();
  const { cemeterySlug: routeCemeterySlug } = useParams<{ cemeterySlug?: string }>();
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<DeceasedPerson[]>([]);
  const [selectedDeceased, setSelectedDeceased] = useState<DeceasedPerson | null>(null);
  const [allDeceased, setAllDeceased] = useState<DeceasedPerson[]>([]);
  const [cemeteryData, setCemeteryData] = useState<CemeteryData | null>(null);
  const [cemeteries, setCemeteries] = useState<CemeterySummary[]>([]);
  const [activeCemeterySlug, setActiveCemeterySlug] = useState<string>('');
  const [pendingSelectedDeceasedId, setPendingSelectedDeceasedId] = useState<number | null>(null);
  const [visibleSearchResultsCount, setVisibleSearchResultsCount] = useState(SEARCH_RESULTS_PAGE_SIZE);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showReferencePoints, setShowReferencePoints] = useState(true);
  const isDev = import.meta.env.DEV;
  const mapRef = useRef<any>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const suppressSearchResultsRef = useRef(false);
  const deferredSearchValue = useDeferredValue(searchValue);

  const activeCemetery = cemeteries.find((c) => c.slug === activeCemeterySlug) ?? null;
  const mapBounds = {
    name: cemeteryData?.cemeteryName ?? activeCemetery?.name ?? FALLBACK_CEMETERY_BOUNDS.name,
    southwest: (cemeteryData?.southwestLat != null && cemeteryData?.southwestLng != null
      ? [cemeteryData.southwestLat, cemeteryData.southwestLng]
      : activeCemetery?.southwestLat != null && activeCemetery?.southwestLng != null
        ? [activeCemetery.southwestLat, activeCemetery.southwestLng]
      : FALLBACK_CEMETERY_BOUNDS.southwest) as [number, number],
    northeast: (cemeteryData?.northeastLat != null && cemeteryData?.northeastLng != null
      ? [cemeteryData.northeastLat, cemeteryData.northeastLng]
      : activeCemetery?.northeastLat != null && activeCemetery?.northeastLng != null
        ? [activeCemetery.northeastLat, activeCemetery.northeastLng]
      : FALLBACK_CEMETERY_BOUNDS.northeast) as [number, number],
    center: (cemeteryData?.centerLat != null && cemeteryData?.centerLng != null
      ? [cemeteryData.centerLat, cemeteryData.centerLng]
      : activeCemetery?.centerLat != null && activeCemetery?.centerLng != null
        ? [activeCemetery.centerLat, activeCemetery.centerLng]
      : FALLBACK_CEMETERY_BOUNDS.center) as [number, number],
    zoom: cemeteryData?.defaultZoom ?? activeCemetery?.defaultZoom ?? FALLBACK_CEMETERY_BOUNDS.zoom,
  };
  const visibleSearchResults = searchResults.slice(0, visibleSearchResultsCount);
  const hasMoreSearchResults = searchResults.length > visibleSearchResults.length;
  const overlayPlacement = cemeteryData
    ? getOverlayPlacement(cemeteryData)
      ?? createPlacementFromBounds(mapBounds.southwest, mapBounds.northeast, cemeteryData.overlayOpacity ?? 1)
    : null;
  const publicReferencePoints = cemeteryData
    ? cemeteryData.referencePoints
      .filter((point) => point.showOnPublicMap)
      .sort((left, right) => left.name.localeCompare(right.name, 'sl') || (left.id - right.id))
      .map((point) => {
        let position: [number, number] | null = null;

        if (Number.isFinite(point.latitude) && Number.isFinite(point.longitude)) {
          position = [point.latitude as number, point.longitude as number];
        } else if (overlayPlacement && Number.isFinite(point.x) && Number.isFinite(point.y)) {
          position = projectReferencePoint({ x: point.x as number, y: point.y as number }, overlayPlacement);
        }

        return {
          ...point,
          position,
          markerIcon: createReferencePointMapIcon(point.iconKey, point.name),
        };
      })
      .filter((point): point is (typeof cemeteryData.referencePoints[number] & { position: [number, number]; markerIcon: L.DivIcon }) => point.position != null)
    : [];

  useEffect(() => {
    if (!mapRef.current || !activeCemeterySlug) {
      return;
    }

    const timer = setTimeout(() => {
      mapRef.current?.setView(mapBounds.center, mapBounds.zoom, { animate: true });
      mapRef.current?.invalidateSize?.();
    }, 0);

    return () => clearTimeout(timer);
  }, [activeCemeterySlug, mapBounds.center, mapBounds.zoom]);

  const getOrCreateSessionId = (): string => {
    const storageKey = 'publicSearchSessionId';
    const existing = sessionStorage.getItem(storageKey);
    if (existing) {
      return existing;
    }

    const generated = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    sessionStorage.setItem(storageKey, generated);
    return generated;
  };

  const normalizeSlug = (value?: string): string => (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const logVisit = async (eventType: 'page_view' | 'selection', deceasedId?: number, cemeterySlug?: string) => {
    try {
      await visitsApi.logVisit({
        eventType,
        sessionId: getOrCreateSessionId(),
        deceasedId,
        cemeterySlug,
      });
    } catch (error) {
      console.error('Visit tracking failed:', error);
    }
  };

  // Helper function to extract year from date string or return null
  const extractYear = (dateString?: string): string | null => {
    if (!dateString) return null;
    
    // Try to extract 4-digit year from the string
    const yearMatch = dateString.match(/\d{4}/);
    return yearMatch ? yearMatch[0] : null;
  };

  // Helper function to format display name with years
  const formatDisplayName = (deceased: DeceasedPerson): string => {
    const birthYear = extractYear(deceased.birthDate) || extractYear(deceased.displayBirthDate) || '?';
    const deathYear = extractYear(deceased.deathDate) || extractYear(deceased.displayDeathDate) || '?';
    
    return `${deceased.fullName} (${birthYear}-${deathYear})`;
  };

  const sortSearchResults = (results: DeceasedPerson[]): DeceasedPerson[] => {
    return [...results].sort((left, right) => {
      const leftIsActive = left.cemeterySlug === activeCemeterySlug;
      const rightIsActive = right.cemeterySlug === activeCemeterySlug;

      if (leftIsActive !== rightIsActive) {
        return leftIsActive ? -1 : 1;
      }

      if (!leftIsActive && !rightIsActive) {
        const cemeteryCompare = (left.cemeteryName ?? '').localeCompare(right.cemeteryName ?? '', 'sl');
        if (cemeteryCompare !== 0) {
          return cemeteryCompare;
        }
      }

      return formatDisplayName(left).localeCompare(formatDisplayName(right), 'sl');
    });
  };

  const matchesSearch = (deceased: DeceasedPerson, value: string): boolean => {
    const tokens = value
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (tokens.length === 0) {
      return true;
    }

    const birthYear = extractYear(deceased.birthDate) || extractYear(deceased.displayBirthDate) || '';
    const deathYear = extractYear(deceased.deathDate) || extractYear(deceased.displayDeathDate) || '';
    const reverseName = [
      deceased.lastName,
      deceased.middleName,
      deceased.firstName,
    ].filter(Boolean).join(' ').toLowerCase();
    const reverseNameWithMaiden = deceased.maidenName
      ? `${deceased.lastName} ${deceased.maidenName} ${deceased.middleName || ''} ${deceased.firstName}`.toLowerCase().replace(/\s+/g, ' ').trim()
      : '';
    const searchableFields = [
      formatDisplayName(deceased).toLowerCase(),
      deceased.fullName.toLowerCase(),
      deceased.firstName.toLowerCase(),
      deceased.lastName.toLowerCase(),
      reverseName,
      reverseNameWithMaiden,
      deceased.maidenName?.toLowerCase() ?? '',
      deceased.cemeteryName?.toLowerCase() ?? '',
      deceased.graveCode?.toLowerCase() ?? '',
      birthYear,
      deathYear,
      `${deceased.fullName} ${birthYear} ${deathYear}`.toLowerCase().trim(),
    ];

    return tokens.every((token) => searchableFields.some((field) => field.includes(token)));
  };

  useEffect(() => {
    const initializeCemeteryContext = async () => {
      try {
        const [list, defaultCemetery] = await Promise.all([
          cemeteryApi.list(false),
          cemeteryApi.getDefault(),
        ]);

        setCemeteries(list);

        const requestedSlug = normalizeSlug(routeCemeterySlug);
        const matched = requestedSlug ? list.find((c) => c.slug === requestedSlug) : null;
        const resolvedSlug = matched?.slug ?? defaultCemetery.slug;

        setActiveCemeterySlug(resolvedSlug);

        if (requestedSlug && !matched) {
          navigate(`/${defaultCemetery.slug}`, { replace: true });
        }
      } catch (error) {
        console.error('Error loading cemetery context:', error);
      }
    };

    void initializeCemeteryContext();
  }, [routeCemeterySlug, navigate]);

  useEffect(() => {
    const loadSearchDataset = async () => {
      await loadAllDeceased();
    };

    void loadSearchDataset();
  }, []);

  useEffect(() => {
    if (!activeCemeterySlug) {
      return;
    }

    const loadPublicContext = async () => {
      setSelectedDeceased(null);
      setSearchValue('');
      setShowSearchResults(false);

      await loadCemeteryData(activeCemeterySlug);

      const pageViewLoggedKey = `publicSearchPageViewLogged:${activeCemeterySlug}`;
      if (!sessionStorage.getItem(pageViewLoggedKey)) {
        await logVisit('page_view', undefined, activeCemeterySlug);
        sessionStorage.setItem(pageViewLoggedKey, '1');
      }
    };

    void loadPublicContext();
  }, [activeCemeterySlug]);

  useEffect(() => {
    if (searchValue.trim()) {
      return;
    }

    setSearchResults(sortSearchResults(allDeceased));
  }, [allDeceased, activeCemeterySlug, searchValue]);

  useEffect(() => {
    const trimmedValue = deferredSearchValue.trim();

    if (!trimmedValue) {
      startTransition(() => {
        setSearchResults(sortSearchResults(allDeceased));
        setVisibleSearchResultsCount(SEARCH_RESULTS_PAGE_SIZE);
      });
      return;
    }

    const filtered = sortSearchResults(allDeceased.filter((deceased) => matchesSearch(deceased, trimmedValue)));

    startTransition(() => {
      setSearchResults(filtered);
      setVisibleSearchResultsCount(SEARCH_RESULTS_PAGE_SIZE);
      if (suppressSearchResultsRef.current) {
        suppressSearchResultsRef.current = false;
        setShowSearchResults(false);
        return;
      }

      setShowSearchResults(filtered.length > 0);
    });
  }, [deferredSearchValue, allDeceased, activeCemeterySlug]);

  useEffect(() => {
    if (pendingSelectedDeceasedId == null || !activeCemeterySlug) {
      return;
    }

    const pendingDeceased = allDeceased.find(
      (deceased) => deceased.id === pendingSelectedDeceasedId && deceased.cemeterySlug === activeCemeterySlug,
    );

    if (!pendingDeceased) {
      return;
    }

    setSelectedDeceased(pendingDeceased);
    setSearchValue(formatDisplayName(pendingDeceased));
    setShowDetailsPopup(!pendingDeceased.latitude || !pendingDeceased.longitude);
    setPendingSelectedDeceasedId(null);
  }, [pendingSelectedDeceasedId, activeCemeterySlug, allDeceased]);

  // Click outside handler to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadCemeteryData = async (cemeterySlug: string) => {
    try {
      const data = await cemeteryApi.getPublicCemeteryData(cemeterySlug);
      if (data) {
        setCemeteryData(data);
      } else {
        setCemeteryData(null);
      }
    } catch (error) {
      console.error('Error loading cemetery data:', error);
    }
  };

  const loadAllDeceased = async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
      const response = await fetch(`${baseUrl}/publicdeceased/search`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load deceased persons');
      }
      
      const data = await response.json();
      setAllDeceased(data);
    } catch (error) {
      console.error('Error loading deceased persons:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    const trimmedValue = value.trim();
    
    // Clear selection when search is cleared
    if (!trimmedValue) {
      setSelectedDeceased(null);
      setShowSearchResults(false);
      setVisibleSearchResultsCount(SEARCH_RESULTS_PAGE_SIZE);
      
      // Reset map view to cemetery center
      if (mapRef.current) {
        mapRef.current.flyTo(mapBounds.center, mapBounds.zoom, {
          duration: 1.5
        });
      }
      return;
    }

    setShowSearchResults(true);
  };

  const handleSelectDeceased = (deceased: DeceasedPerson) => {
    suppressSearchResultsRef.current = true;
    setSearchValue(formatDisplayName(deceased));
    setShowSearchResults(false);
    void logVisit('selection', deceased.id, deceased.cemeterySlug ?? activeCemeterySlug);

    if (deceased.cemeterySlug && deceased.cemeterySlug !== activeCemeterySlug) {
      setPendingSelectedDeceasedId(deceased.id);
      setSelectedDeceased(null);
      setShowDetailsPopup(false);
      setCemeteryData(null);
      navigate(`/${deceased.cemeterySlug}`);
      return;
    }

    setSelectedDeceased(deceased);

    if (!deceased.latitude || !deceased.longitude) {
      setShowDetailsPopup(true);
    } else {
      setShowDetailsPopup(false);
    }
  };

  const handleMarkerClick = (deceased: DeceasedPerson) => {
    setSelectedDeceased(deceased);
    setSearchValue(formatDisplayName(deceased)); // Use formatted name with years
    setShowDetailsPopup(true);
  };

  const closeDetailsPopup = () => {
    setShowDetailsPopup(false);
  };

  return (
    <div className="public-search-container">
      {/* Dev-only overlay toggle - top left corner */}
      {isDev && (
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: 'rgba(0,0,0,0.6)',
          color: '#fff',
          padding: '6px 12px',
          borderRadius: '8px',
          fontSize: '13px',
          userSelect: 'none'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
            <input
              type="checkbox"
              checked={showOverlay}
              onChange={(e) => setShowOverlay(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Overlay
          </label>
        </div>
      )}

      {/* Search Bar - Floating Overlay */}
      <div className="search-bar-container">
        <div className="search-bar-content">
          <div className="search-title">
            <SelectBox
              className="cemetery-title-select"
              items={cemeteries}
              valueExpr="slug"
              displayExpr="name"
              value={activeCemeterySlug || null}
              placeholder={mapBounds.name || 'Izberi pokopališče'}
              width="100%"
              height={56}
              stylingMode="underlined"
              onValueChanged={(e) => {
                const slug = (e.value as string) || '';
                if (!slug || slug === activeCemeterySlug) {
                  return;
                }

                setCemeteryData(null);
                navigate(`/${slug}`);
              }}
            />
          </div>
          
          <div className="search-input-wrapper" style={{ position: 'relative' }} ref={searchWrapperRef}>
            <TextBox
              className="public-search-textbox"
              value={searchValue}
              onValueChanged={(e) => handleSearch(e.value)}
              valueChangeEvent="input"
              placeholder="🔍 Iskanje pokojnika..."
              showClearButton={true}
              stylingMode="filled"
              width="100%"
              height={48}
            />
            
            {/* Custom search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                {visibleSearchResults.map((result, index) => (
                  <div
                    key={`${result.id}-${result.cemeterySlug ?? 'unknown'}`}
                    className="search-result-item"
                    onClick={() => handleSelectDeceased(result)}
                    style={{
                      borderBottom: index < visibleSearchResults.length - 1 || hasMoreSearchResults ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <span className="search-result-text">{formatDisplayName(result)}</span>
                    {result.cemeterySlug !== activeCemeterySlug && result.cemeteryName && (
                      <span className="search-result-tag">{result.cemeteryName}</span>
                    )}
                  </div>
                ))}
                {hasMoreSearchResults && (
                  <button
                    type="button"
                    className="search-results-more"
                    onClick={() => setVisibleSearchResultsCount((current) => current + SEARCH_RESULTS_PAGE_SIZE)}
                  >
                    Prikaži več rezultatov...
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map Container - Full Screen */}
      <div className="map-container" style={{ position: 'relative' }}>
        <div className="reference-points-toggle" role="group" aria-label="Prikaz referenčnih točk">
          <button
            type="button"
            className={`reference-points-toggle-button ${showReferencePoints ? 'is-active' : 'is-inactive'}`}
            onClick={() => setShowReferencePoints((prev) => !prev)}
            aria-pressed={showReferencePoints}
            aria-label={showReferencePoints ? 'Skrij referenčne točke' : 'Prikaži referenčne točke'}
            title={showReferencePoints ? 'Skrij referenčne točke' : 'Prikaži referenčne točke'}
          >
            <span aria-hidden="true">📍</span>
          </button>
        </div>

        {/* Info Button - Bottom Right (above zoom controls) */}
        {selectedDeceased && selectedDeceased.latitude && selectedDeceased.longitude && (
          <button
            onClick={() => handleMarkerClick(selectedDeceased)}
            className="info-button"
            style={{
              position: 'absolute',
              right: '80px',
              zIndex: 1000,
              width: '90px',
              height: '64px',
              backgroundColor: '#fff',
              border: '2px solid rgba(0,0,0,0.2)',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: '600',
              lineHeight: '1.3',
              textAlign: 'center',
              padding: '6px 8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Klikni za prikaz slike"
          >
            Klikni za prikaz slike
          </button>
        )}
        
        <MapContainer
          key={activeCemeterySlug || 'default-cemetery'}
          center={mapBounds.center}
          zoom={mapBounds.zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={false}
          ref={mapRef}
          maxZoom={22}
          whenReady={() => {
            // Prevent map from recalculating on window resize caused by popups
            if (mapRef.current) {
              setTimeout(() => {
                mapRef.current?.invalidateSize();
              }, 100);
            }
          }}
        >
          {/* Zoom Control - Bottom Right */}
          <ZoomControl position="bottomright" />
          
          {/* Base Layer: Esri World Imagery (free satellite imagery) */}
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={20}
            minZoom={3}
          />
          
          {/* ALTERNATIVE: OpenStreetMap (street map, not satellite) */}
          {/* <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            minZoom={3}
          /> */}
          
          {/* BACKUP: Sentinel-2 Cloudless (lower resolution, kept for fallback) */}
          {/* <TileLayer
            attribution='<a href="https://s2maps.eu" target="_blank">Sentinel-2 cloudless</a> by <a href="https://eox.at" target="_blank">EOX IT Services</a>'
            url="https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg"
            maxZoom={22}
            maxNativeZoom={18}
            minZoom={3}
          /> */}
          
          {/* Drone Photo Overlay */}
          {cemeteryData?.photoPath && overlayPlacement && showOverlay && (
            <DistortableImageLayer
              imageUrl={`${window.location.origin}${cemeteryData.photoPath}`}
              placement={overlayPlacement}
              editable={false}
              opacity={1}
            />
          )}

          {showReferencePoints && publicReferencePoints.map((point) => (
            <Marker
              key={point.id}
              position={point.position}
              icon={point.markerIcon}
              title={point.name}
            >
              {point.description?.trim() && (
                <Tooltip direction="top" offset={[0, -28]} opacity={1} className="public-reference-point-tooltip" sticky>
                  {point.description}
                </Tooltip>
              )}
            </Marker>
          ))}

          {/* Fly to selected location */}
          {selectedDeceased && selectedDeceased.latitude && selectedDeceased.longitude && (
            <FlyToLocation position={[selectedDeceased.latitude, selectedDeceased.longitude]} />
          )}

          {/* Marker for selected deceased */}
          {selectedDeceased && selectedDeceased.latitude && selectedDeceased.longitude && (
            <Marker 
              position={[selectedDeceased.latitude, selectedDeceased.longitude]}
              eventHandlers={{
                click: () => handleMarkerClick(selectedDeceased)
              }}
            />
          )}


        </MapContainer>
      </div>

      {/* Fullscreen Details Popup */}
      <Popup
        visible={showDetailsPopup}
        onHiding={closeDetailsPopup}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showTitle={true}
        showCloseButton={true}
        title={selectedDeceased?.fullName || ''}
        width="90%"
        height="90%"
        maxWidth={1200}
        position={{ my: 'center', at: 'center', of: window }}
        shading={true}
        shadingColor="rgba(0, 0, 0, 0.5)"
        wrapperAttr={{ style: { zIndex: 2000 } }}
        onShown={() => {
          // Prevent map dimension changes when popup opens
          document.body.style.overflow = 'hidden';
        }}
        onHidden={() => {
          // Restore scroll and invalidate map size when popup closes
          document.body.style.overflow = '';
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize();
            }
          }, 100);
        }}
      >
        <div style={{ padding: '0px', height: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {selectedDeceased && (
            <>
              {/* Grave Photo - Large Display */}
              {selectedDeceased.photoPath && (
                <div style={{ flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', backgroundColor: '#000', borderRadius: '8px', minHeight: '0' }}>
                  <img 
                    src={`${import.meta.env.VITE_API_BASE_URL?.startsWith('http') ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') : window.location.origin}${selectedDeceased.photoPath}`}
                    alt={`Grob ${selectedDeceased.fullName}`}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              )}
              
              {/* Details Section */}
              <div style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '24px', color: '#333' }}>{selectedDeceased.fullName}</h2>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', fontSize: '16px' }}>
                  {selectedDeceased.displayBirthDate && (
                    <div>
                      <strong style={{ color: '#666' }}>Rojstvo:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.displayBirthDate}</div>
                    </div>
                  )}
                  
                  {selectedDeceased.displayDeathDate && (
                    <div>
                      <strong style={{ color: '#666' }}>Smrt:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.displayDeathDate}</div>
                    </div>
                  )}
                  
                  {/* {selectedDeceased.age && (
                    <div>
                      <strong style={{ color: '#666' }}>Starost:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.age} let</div>
                    </div>
                  )} */}
                  
                  {selectedDeceased.graveCode && (
                    <div>
                      <strong style={{ color: '#666' }}>Grob:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.graveCode}</div>
                    </div>
                  )}
                  
                  {selectedDeceased.graveType && (
                    <div>
                      <strong style={{ color: '#666' }}>Tip groba:</strong>
                      <div style={{ fontSize: '18px', marginTop: '5px' }}>{selectedDeceased.graveType}</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Popup>
    </div>
  );
};

export default PublicSearch;
